/* eslint-disable camelcase */
import { register_information as emptyRegisterInformation } from '@/views/Recipients/Form/fields'
import { isRegisterInformationEditableStatus } from '@/enums/recipient-status.enum'
import { unformatCnpj, unformatCpf } from '@/utils/formatCpfCnpj.utils'

export const REGISTER_INFORMATION_TYPE = {
  INDIVIDUAL: 'individual',
  CORPORATION: 'corporation'
}

const INDIVIDUAL_TYPES = ['individual', 'física', 'fisica']

const toLowerCase = value =>
  typeof value === 'string' ? value.toLowerCase() : ''

const isEmpty = value =>
  value === undefined || value === null || value === '' || value === '-'

const valueOrEmpty = value => (isEmpty(value) ? '' : value)

const firstFilled = (...values) => values.find(value => !isEmpty(value)) ?? ''

const BR_DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})/

const toDateOnly = value => {
  if (typeof value !== 'string' || !value) return ''
  const brDate = BR_DATE_REGEX.exec(value)
  return brDate ? `${brDate[3]}-${brDate[2]}-${brDate[1]}` : value.slice(0, 10)
}

export function resolveRegisterInformationType (detail = {}) {
  const type =
    toLowerCase(detail.register_information?.type) || toLowerCase(detail.type)

  return INDIVIDUAL_TYPES.includes(type)
    ? REGISTER_INFORMATION_TYPE.INDIVIDUAL
    : REGISTER_INFORMATION_TYPE.CORPORATION
}

export function buildRegisterInformationForm (detail = {}) {
  const info = detail.register_information || {}
  const type = resolveRegisterInformationType(detail)
  const address =
    type === REGISTER_INFORMATION_TYPE.INDIVIDUAL
      ? info.address
      : info.main_address
  const phones = info.phone_numbers?.length
    ? info.phone_numbers
    : emptyRegisterInformation.phone_numbers

  return {
    type,
    name: firstFilled(info.name, detail.name),
    email: firstFilled(info.email, detail.email),
    document: firstFilled(info.document, detail.document),
    description: valueOrEmpty(detail.description),
    site_url: valueOrEmpty(info.site_url),
    birth_date: toDateOnly(info.birthdate),
    monthly_income: valueOrEmpty(info.monthly_income),
    professional_occupation: valueOrEmpty(info.professional_occupation),
    mother_name: valueOrEmpty(info.mother_name),
    company_name: valueOrEmpty(info.company_name),
    trading_name: valueOrEmpty(info.trading_name),
    annual_revenue: valueOrEmpty(info.annual_revenue),
    founding_date: toDateOnly(info.founding_date),
    phone_numbers: phones.map(phone => ({ ...phone })),
    address: { ...emptyRegisterInformation.address, ...address },
    managing_partners: info.managing_partners || []
  }
}

const FILL_IF_EMPTY_FIELDS = [
  'birth_date',
  'monthly_income',
  'professional_occupation',
  'annual_revenue'
]

const ALWAYS_LOCKED_FIELDS = [
  'trading_name',
  'founding_date',
  'mother_name',
  'description'
]

const BFF_FIELD_NAMES = { birth_date: 'birthdate' }

const getFieldValue = (info, field) => info[BFF_FIELD_NAMES[field] ?? field]

const hasLockedValue = value => !isEmpty(value) && value !== 0

export function getRegisterInformationLocks (detail = {}) {
  const info = detail.register_information || {}
  const locks = {}

  FILL_IF_EMPTY_FIELDS.forEach(field => {
    locks[field] = hasLockedValue(getFieldValue(info, field))
  })
  ALWAYS_LOCKED_FIELDS.forEach(field => {
    locks[field] = true
  })

  return locks
}

export function canEditRegisterInformation ({
  flagEnabled,
  isKycAccount,
  rawStatus
}) {
  return (
    !!flagEnabled &&
    !!isKycAccount &&
    isRegisterInformationEditableStatus(rawStatus)
  )
}

const INDIVIDUAL_BODY_FIELDS = [
  'email',
  'name',
  'site_url',
  'birth_date',
  'monthly_income',
  'professional_occupation'
]

const CORPORATION_BODY_FIELDS = [
  'email',
  'company_name',
  'trading_name',
  'site_url',
  'annual_revenue'
]

const NUMBER_FIELDS = ['monthly_income', 'annual_revenue']

const onlyDigits = value => String(value ?? '').replace(/\D/g, '')

const toNumber = value => {
  if (typeof value === 'number') return value
  const digits = onlyDigits(value)
  return digits ? Number(digits) : null
}

const formatFieldValue = (field, value) => {
  if (NUMBER_FIELDS.includes(field)) return toNumber(value)
  if (field === 'site_url') return value || null
  return value
}

const DEFAULT_COUNTRY = 'BR'

const withCountry = address => ({
  ...address,
  country: address?.country || DEFAULT_COUNTRY
})

const DATE_FIELDS = ['birth_date']

const toDateTime = value => {
  const date = toDateOnly(value)
  return date ? `${date}T00:00:00` : value
}

const toManagingPartnerBody = ({ birthdate, ...partner }) => {
  return {
    ...partner,
    ...(birthdate !== undefined && { birth_date: toDateTime(birthdate) }),
    ...(partner.address && { address: withCountry(partner.address) })
  }
}

export function buildRegisterInformationBody (form, detail = {}) {
  const info = detail.register_information || {}
  const locks = getRegisterInformationLocks(detail)
  const isIndividual = form.type === REGISTER_INFORMATION_TYPE.INDIVIDUAL
  const fields = isIndividual ? INDIVIDUAL_BODY_FIELDS : CORPORATION_BODY_FIELDS
  const [firstPhone] = form.phone_numbers
  const otherPhones = (info.phone_numbers || []).slice(1)

  const body = {
    type: form.type,
    document: isIndividual
      ? unformatCpf(String(form.document))
      : unformatCnpj(String(form.document)),
    [isIndividual ? 'address' : 'main_address']: withCountry(form.address),
    phone_numbers: [
      {
        ...firstPhone,
        ddd: onlyDigits(firstPhone.ddd),
        number: onlyDigits(firstPhone.number)
      },
      ...otherPhones
    ]
  }

  fields.forEach(field => {
    const value = locks[field]
      ? getFieldValue(info, field)
      : formatFieldValue(field, form[field])

    body[field] = DATE_FIELDS.includes(field) ? toDateTime(value) : value
  })

  if (!isIndividual) {
    body.managing_partners = (info.managing_partners || []).map(
      toManagingPartnerBody
    )
  }

  return body
}
